import { formatDistanceToNow } from "date-fns";
import {
  UserPlus, CheckCircle2, XCircle, ShieldOff, Mail,
  CalendarPlus, CalendarCheck, CalendarX, CalendarMinus,
  Building2, Trash2, FileUp, FileX, FileCheck,
  AlertTriangle, MessageSquare, Bell, ShieldAlert,
  BookOpen, DollarSign, Settings, UserCheck, UserMinus,
  ShieldCheck, CreditCard, Activity,
} from "lucide-react";
import type { ActivityLogEntry } from "@/hooks/useActivityFeed";
import type { ActivityActionType } from "@/lib/activityLogger";
import { ACTION_LABELS, getActivityColor } from "@/lib/activityLogger";

// ─── Icon lookup map ──────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ElementType> = {
  UserPlus, CheckCircle2, XCircle, ShieldOff, Mail,
  CalendarPlus, CalendarCheck, CalendarX, CalendarMinus,
  Building2, Trash2, FileUp, FileX, FileCheck,
  AlertTriangle, MessageSquare, Bell, ShieldAlert,
  BookOpen, DollarSign, Settings, UserCheck, UserMinus,
  ShieldCheck, CreditCard,
};

const ACTION_ICON_NAME: Record<ActivityActionType, string> = {
  host_registered:           "UserPlus",
  host_approved:             "CheckCircle2",
  host_rejected:             "XCircle",
  host_suspended:            "ShieldOff",
  host_invited:              "Mail",
  booking_created:           "CalendarPlus",
  booking_approved:          "CalendarCheck",
  booking_rejected:          "CalendarX",
  booking_blocked:           "CalendarX",
  booking_cancelled:         "CalendarMinus",
  booking_completed:         "CalendarCheck",
  unit_added:                "Building2",
  unit_updated:              "Building2",
  unit_deleted:              "Trash2",
  document_uploaded:         "FileUp",
  document_deleted:          "FileX",
  document_approved:         "FileCheck",
  document_rejected:         "FileX",
  dispute_filed:             "AlertTriangle",
  dispute_updated:           "MessageSquare",
  dispute_resolved:          "CheckCircle2",
  compliance_notice_issued:  "Bell",
  compliance_warning_issued: "AlertTriangle",
  compliance_penalty_issued: "ShieldAlert",
  bylaw_updated:             "BookOpen",
  fee_changed:               "DollarSign",
  config_updated:            "Settings",
  member_invited:            "UserPlus",
  member_accepted:           "UserCheck",
  member_removed:            "UserMinus",
  registration_validated:    "ShieldCheck",
  registration_expired:      "ShieldOff",
  subscription_activated:    "CreditCard",
  subscription_cancelled:    "CreditCard",
};

// ─── Helper: build a human-readable description from metadata ─────────────────

function buildDescription(entry: ActivityLogEntry): string {
  const meta = entry.metadata as Record<string, string>;
  const actor = entry.actor?.full_name ?? entry.actor?.email ?? "System";

  switch (entry.action_type) {
    case "host_registered":
      return `${meta.host_name ?? actor} registered as a host for Unit ${meta.unit_number ?? "—"}`;
    case "host_approved":
      return `${actor} approved host ${meta.host_name ?? ""} for Unit ${meta.unit_number ?? "—"}`;
    case "host_rejected":
      return `${actor} rejected host ${meta.host_name ?? ""} for Unit ${meta.unit_number ?? "—"}`;
    case "host_invited":
      return `${actor} invited ${meta.email ?? "someone"} as a host for Unit ${meta.unit_number ?? "—"}`;
    case "booking_created":
      return `${meta.guest_name ?? "A guest"} submitted a booking for Unit ${meta.unit_number ?? "—"} (${meta.nights ?? "?"}n)`;
    case "booking_approved":
      return `${actor} approved ${meta.guest_name ?? "a booking"}'s stay at Unit ${meta.unit_number ?? "—"}`;
    case "booking_rejected":
    case "booking_blocked":
      return `Booking for Unit ${meta.unit_number ?? "—"} was blocked`;
    case "booking_cancelled":
      return `Booking for Unit ${meta.unit_number ?? "—"} was cancelled`;
    case "unit_added":
      return `${actor} added Unit ${meta.unit_number ?? "—"}`;
    case "unit_updated":
      return `${actor} updated Unit ${meta.unit_number ?? "—"}`;
    case "document_uploaded":
      return `${actor} uploaded ${meta.document_name ?? "a document"} for Unit ${meta.unit_number ?? "—"}`;
    case "document_approved":
      return `${actor} approved ${meta.document_name ?? "a document"} for Unit ${meta.unit_number ?? "—"}`;
    case "document_rejected":
      return `${actor} rejected ${meta.document_name ?? "a document"} for Unit ${meta.unit_number ?? "—"}`;
    case "dispute_filed":
      return `${meta.host_name ?? actor} filed a ${meta.dispute_type ?? ""} dispute`;
    case "dispute_resolved":
      return `${actor} resolved dispute #${(entry.entity_id ?? "").slice(0, 6).toUpperCase()}`;
    case "compliance_notice_issued":
    case "compliance_warning_issued":
    case "compliance_penalty_issued":
      return `${actor} issued a compliance ${entry.action_type.replace("compliance_","").replace("_issued","")} for Unit ${meta.unit_number ?? "—"}`;
    case "member_invited":
      return `${actor} invited ${meta.email ?? "someone"} as ${meta.role ?? "a member"}`;
    case "member_accepted":
      return `${meta.name ?? "Someone"} accepted an invitation as ${meta.role ?? "a member"}`;
    case "registration_validated":
      return `Registry validation passed for ${meta.host_name ?? "a host"} (Unit ${meta.unit_number ?? "—"})`;
    default:
      return ACTION_LABELS[entry.action_type as ActivityActionType] ?? entry.action_type;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

interface ActivityItemProps {
  entry: ActivityLogEntry;
  compact?: boolean;
}

export function ActivityItem({ entry, compact = false }: ActivityItemProps) {
  const iconName = ACTION_ICON_NAME[entry.action_type as ActivityActionType] ?? "Activity";
  const Icon = ICON_MAP[iconName] ?? Activity;
  const colorClass = getActivityColor(entry.action_type as ActivityActionType);
  const description = buildDescription(entry);
  const timeAgo = formatDistanceToNow(new Date(entry.created_at), { addSuffix: true });

  if (compact) {
    return (
      <div className="flex items-start gap-2 py-1.5">
        <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${colorClass}`} />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-foreground leading-snug truncate">{description}</p>
          <p className="text-[10px] text-muted-foreground">{timeAgo}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 py-3 border-b last:border-0">
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted`}>
        <Icon className={`h-4 w-4 ${colorClass}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground leading-snug">{description}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {entry.actor?.full_name && (
            <span className="text-xs text-muted-foreground">{entry.actor.full_name}</span>
          )}
          <span className="text-[10px] text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground">{timeAgo}</span>
        </div>
      </div>
    </div>
  );
}
